import { createFileRoute, redirect } from '@tanstack/react-router';
import * as React from 'react';
import {
  type ColumnDef,
  type OnChangeFn,
  type RowSelectionState,
} from '@tanstack/react-table';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import {
  MagnifyingGlassIcon,
  ArrowClockwiseIcon,
  ArrowsLeftRightIcon,
  CoinsIcon,
  ToggleLeftIcon,
  QuestionIcon,
  ClockCounterClockwiseIcon,
} from '@phosphor-icons/react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PaymentService, type AdminSubscription } from '@/api/payments-service';
import { adminSubscriptionsOptions } from '@/hooks/query-options';
import { formatBucketLabel } from '@/lib/plans';
import { cn } from '@/utils/cn';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { GrantCreditsDialog } from '@/components/super-admin/grant-credits-dialog';
import { RetentionOverrideDialog } from '@/components/super-admin/retention-override-dialog';
import { MigratePaidDialog } from '@/components/super-admin/migrate-paid-dialog';
import { OrgFeatureFlags } from '@/components/super-admin/org-feature-flags';

export const Route = createFileRoute(
  '/_auth/_side-layout/super-admin/subscriptions',
)({
  component: SuperAdminSubscriptions,
  beforeLoad: async ({ context }) => {
    if (context.platformRole !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
});

function TruncatedCell({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [text]);

  const content = (
    <span ref={ref} className={cn('block max-w-[200px] truncate', className)}>
      {text}
    </span>
  );

  if (!isTruncated) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}

const PLAN_NAMES: Record<string, string> = {
  FREE: 'Free',
  ESSENTIALS: 'Essentials',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
};

const PAGE_SIZE = 20;

type FilterMode = 'all' | 'free' | 'paid' | 'unsynced';

const FILTER_OPTIONS: { value: FilterMode; label: string; title: string }[] = [
  { value: 'all', label: 'All', title: 'Show every organization' },
  { value: 'free', label: 'Free', title: 'Only FREE-plan orgs' },
  { value: 'paid', label: 'Paid', title: 'Only paid (non-FREE) orgs' },
  {
    value: 'unsynced',
    label: 'Unsynced',
    title: 'Orgs with no Stripe customer id yet — these need a sync or migrate',
  },
];

// Derived migration-status badge per row. Computed entirely from row data —
// no extra Stripe round-trips. Renders the most "needs attention" state at
// the top of the column so it sorts naturally with the eye.
function statusBadge(sub: AdminSubscription): {
  variant: 'warning' | 'neutral' | 'success';
  label: string;
} {
  if (!sub.customerId) return { variant: 'warning', label: 'Not Synced' };
  if (sub.plan === 'FREE') return { variant: 'neutral', label: 'Free Synced' };
  if (sub.couponId) return { variant: 'success', label: 'Paid (Discounted)' };
  return { variant: 'success', label: 'Paid' };
}

function SuperAdminSubscriptions() {
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<FilterMode>('all');
  const [page, setPage] = React.useState(0);
  const [syncingOrg, setSyncingOrg] = React.useState<string | null>(null);
  const [bulkSyncing, setBulkSyncing] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  // Org whose feature-flags dialog is open (null = closed). A single dialog is
  // rendered at the bottom and driven by this target, not one per row.
  const [featureFlagsTarget, setFeatureFlagsTarget] =
    React.useState<AdminSubscription | null>(null);
  const [grantCreditsTarget, setGrantCreditsTarget] =
    React.useState<AdminSubscription | null>(null);
  const [retentionTarget, setRetentionTarget] =
    React.useState<AdminSubscription | null>(null);

  // Migrate queue: an array of orgs to walk through one-by-one. The dialog
  // is open whenever there's a current target. Submitting or clicking Skip
  // advances `migrateIndex`. When the index runs past the end we clear the
  // queue, which closes the dialog.
  const [migrateQueue, setMigrateQueue] = React.useState<AdminSubscription[]>(
    [],
  );
  const [migrateIndex, setMigrateIndex] = React.useState(0);
  const migrateTarget = migrateQueue[migrateIndex] ?? null;

  const queryClient = useQueryClient();

  const { data: subscriptions = [], isLoading } = useQuery(
    adminSubscriptionsOptions(),
  );

  const filtered = React.useMemo(() => {
    let rows = subscriptions;
    if (filter === 'free') rows = rows.filter((s) => s.plan === 'FREE');
    else if (filter === 'paid') rows = rows.filter((s) => s.plan !== 'FREE');
    else if (filter === 'unsynced') rows = rows.filter((s) => !s.customerId);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (s) =>
          s.organizationName.toLowerCase().includes(q) ||
          s.slug.toLowerCase().includes(q) ||
          s.plan.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [subscriptions, filter, search]);

  // Slice the filtered list for the current page. Client-side pagination is
  // fine here — total org count fits comfortably in a single API response.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = React.useMemo(
    () => filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [filtered, safePage],
  );

  // Header checkbox + "select all" operate on the visible page only — what
  // the admin sees is what they get. To bulk-act across pages, change the
  // page or filter and select again. The DataTable is controlled, so we
  // project the existing `selected` Set down to the current page's rows as a
  // RowSelectionState (keyed by org id). The Set stays the source of truth
  // for the bulk-action bar (selectedRows / selectedFree / selectedPaid) and
  // can hold selections from other pages without the table needing to know.
  const rowSelection = React.useMemo<RowSelectionState>(() => {
    const state: RowSelectionState = {};
    for (const s of pagedRows) {
      if (selected.has(s.organizationId)) state[s.organizationId] = true;
    }
    return state;
  }, [pagedRows, selected]);

  // Fold the table's selection change (a TanStack updater or value, scoped to
  // the current page) back into the page-or-cross-page `selected` Set: add ids
  // the table now marks selected, drop ids on this page it no longer does.
  // Off-page selections in the Set are untouched.
  const onRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    const next =
      typeof updater === 'function' ? updater(rowSelection) : updater;
    setSelected((prev) => {
      const result = new Set(prev);
      for (const s of pagedRows) {
        if (next[s.organizationId]) result.add(s.organizationId);
        else result.delete(s.organizationId);
      }
      return result;
    });
  };

  // Selection partition by plan — drives the smart bulk action bar.
  const selectedRows = React.useMemo(
    () => subscriptions.filter((s) => selected.has(s.organizationId)),
    [subscriptions, selected],
  );
  const selectedFree = selectedRows.filter((s) => s.plan === 'FREE');
  const selectedPaid = selectedRows.filter((s) => s.plan !== 'FREE');

  const openMigrateQueue = (orgs: AdminSubscription[]) => {
    if (orgs.length === 0) return;
    setMigrateQueue(orgs);
    setMigrateIndex(0);
  };

  const advanceMigrateQueue = () => {
    setMigrateIndex((i) => {
      const next = i + 1;
      if (next >= migrateQueue.length) {
        setMigrateQueue([]);
        return 0;
      }
      return next;
    });
  };

  const handleSync = async (sub: AdminSubscription) => {
    setSyncingOrg(sub.organizationId);
    try {
      const result = await PaymentService.syncSubscription(sub.organizationId);
      await queryClient.invalidateQueries({
        queryKey: ['admin-subscriptions'],
      });
      if (result.synced) {
        toast.success(`Synced ${sub.organizationName}`);
      } else {
        toast.info(
          result.message ?? `Nothing to sync for ${sub.organizationName}`,
        );
      }
    } catch (err) {
      const message =
        err instanceof Error && err.message ? err.message : 'Failed to sync';
      // 404 with this exact message is the specific "paid org has no
      // matching Stripe sub" case — surface a one-click migrate CTA.
      if (message.includes('No active subscription found in Stripe')) {
        toast.error(`${sub.organizationName} needs migration`, {
          description: message,
          action: {
            label: 'Migrate now',
            onClick: () => openMigrateQueue([sub]),
          },
        });
      } else {
        toast.error(`Failed to sync ${sub.organizationName}`, {
          description: message,
        });
      }
    } finally {
      setSyncingOrg(null);
    }
  };

  // Bulk sync targets only FREE rows — paid rows go through the migrate
  // queue. Failures are tallied with their server-side message so admins
  // can see exactly why something didn't sync. Failures matching the
  // "needs migration" 404 message get an action button to start the
  // migrate queue.
  const handleBulkSync = async () => {
    if (selectedFree.length === 0 || bulkSyncing) return;
    setBulkSyncing(true);
    let succeeded = 0;
    let unchanged = 0;
    const failures: { sub: AdminSubscription; message: string }[] = [];
    const needsMigration: AdminSubscription[] = [];

    for (const sub of selectedFree) {
      try {
        const result = await PaymentService.syncSubscription(
          sub.organizationId,
        );
        if (result.synced) succeeded++;
        else unchanged++;
      } catch (err) {
        const message =
          err instanceof Error && err.message ? err.message : 'Sync failed';
        failures.push({ sub, message });
        if (message.includes('No active subscription found in Stripe')) {
          needsMigration.push(sub);
        }
      }
    }

    await queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    setBulkSyncing(false);
    setSelected(new Set());

    if (failures.length > 0) {
      const description = failures
        .map((f) => `${f.sub.organizationName}: ${f.message}`)
        .join('\n');
      const failingTitle = `Bulk sync finished with ${failures.length} failure${failures.length === 1 ? '' : 's'}`;
      const subtitle = `${succeeded} synced, ${unchanged} unchanged.`;
      if (needsMigration.length > 0) {
        toast.error(failingTitle, {
          description: `${subtitle}\n\n${description}`,
          action: {
            label: `Migrate ${needsMigration.length} now`,
            onClick: () => openMigrateQueue(needsMigration),
          },
        });
      } else {
        toast.error(failingTitle, {
          description: `${subtitle}\n\n${description}`,
        });
      }
    } else {
      toast.success('Bulk sync complete', {
        description: `${succeeded} synced, ${unchanged} unchanged.`,
      });
    }
  };

  const columns = React.useMemo<ColumnDef<AdminSubscription>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(checked) =>
              table.toggleAllPageRowsSelected(checked === true)
            }
            aria-label="Select all orgs on this page"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(checked) => row.toggleSelected(checked === true)}
            aria-label={`Select ${row.original.organizationName}`}
          />
        ),
      },
      {
        id: 'organization',
        header: 'Organization',
        cell: ({ row }) => {
          const sub = row.original;
          return (
            <div>
              <span className="font-medium">{sub.organizationName}</span>
              <p className="text-muted-foreground text-xs">{sub.slug}</p>
            </div>
          );
        },
      },
      {
        id: 'plan',
        header: 'Plan',
        cell: ({ row }) => {
          const sub = row.original;
          return (
            <Badge
              variant={
                sub.plan === 'FREE'
                  ? 'neutral'
                  : sub.plan === 'PRO'
                    ? 'indigo'
                    : 'default'
              }
            >
              {PLAN_NAMES[sub.plan] ?? sub.plan}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = statusBadge(row.original);
          return <Badge variant={status.variant}>{status.label}</Badge>;
        },
      },
      {
        id: 'eventBucket',
        header: 'Event Bucket',
        cell: ({ row }) => {
          const sub = row.original;
          return sub.eventBucket
            ? formatBucketLabel(sub.eventBucket)
            : sub.plan === 'FREE'
              ? '5k'
              : '—';
        },
      },
      {
        id: 'active',
        header: 'Active',
        cell: ({ row }) => (
          <Badge variant={row.original.active ? 'success' : 'neutral'}>
            {row.original.active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        id: 'stripeCustomer',
        header: 'Stripe Customer',
        cell: ({ row }) => {
          const sub = row.original;
          return (
            <div>
              {sub.customerId ? (
                <TruncatedCell
                  text={sub.customerId}
                  className="text-muted-foreground font-mono"
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
              {sub.ownerEmail && (
                <TruncatedCell
                  text={sub.ownerEmail}
                  className="text-muted-foreground text-xs"
                />
              )}
            </div>
          );
        },
      },
      {
        id: 'coupon',
        header: 'Coupon',
        cell: ({ row }) => {
          const sub = row.original;
          return sub.couponId ? (
            <TruncatedCell
              text={sub.couponId}
              className="text-muted-foreground font-mono"
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'lastPayment',
        header: 'Last Payment',
        cell: ({ row }) => {
          const sub = row.original;
          return sub.lastPayment ? (
            new Date(sub.lastPayment).toLocaleDateString()
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const sub = row.original;
          return (
            <div className="flex items-center justify-end">
              <RowActionsMenu
                actions={[
                  {
                    label: 'Sync from Stripe',
                    icon: (
                      <ArrowClockwiseIcon
                        className={cn(
                          syncingOrg === sub.organizationId && 'animate-spin',
                        )}
                      />
                    ),
                    onSelect: () => handleSync(sub),
                    disabled: syncingOrg === sub.organizationId,
                  },
                  {
                    label: 'Edit Subscription',
                    icon: <ArrowsLeftRightIcon />,
                    onSelect: () => openMigrateQueue([sub]),
                    title:
                      "Edit this org's subscription — set tier, event bucket, coupon, or billing interval. Works for free, synced, and paid orgs.",
                  },
                  {
                    label: 'Feature Flags',
                    icon: <ToggleLeftIcon />,
                    onSelect: () => setFeatureFlagsTarget(sub),
                  },
                  {
                    label: 'Grant Data Credits',
                    icon: <CoinsIcon />,
                    onSelect: () => setGrantCreditsTarget(sub),
                    title:
                      'Grant a Data Credit pack to this org outside the purchase and bonus flows.',
                  },
                  {
                    label: 'Retention Override',
                    icon: <ClockCounterClockwiseIcon />,
                    onSelect: () => setRetentionTarget(sub),
                    title:
                      'Pin or clear how far back this org can query analytics, overriding the plan and lifetime-deal defaults.',
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    // Only syncingOrg needs to refresh the actions cell (drives the spinner +
    // disabled state). handleSync / openMigrateQueue / setFeatureFlagsTarget
    // read the latest closure each render and don't belong in deps.
    [syncingOrg],
  );

  return (
    <>
      <PageHeader
        title="Subscriptions"
        description="View and manage organization billing."
        titleAction={
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                title="How sync, migrate, and proration work"
                aria-label="How sync, migrate, and proration work"
                className="text-muted-foreground hover:text-foreground hover:bg-muted/50 inline-flex size-6 items-center justify-center rounded-full transition-colors"
              >
                <QuestionIcon size={16} weight="regular" />
              </button>
            </SheetTrigger>
            <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
              <SheetHeader>
                <SheetTitle>Subscriptions admin guide</SheetTitle>
                <SheetDescription>
                  How sync, migrate, and proration interact. Open this any time
                  you're not sure which action you want.
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-4 px-4 pb-6 text-sm">
                <section>
                  <h3 className="text-foreground font-semibold">
                    Sync (per row + bulk)
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    Pulls the latest subscription state from the current Stripe
                    account into the local DB. For free orgs with no Stripe
                    customer yet, provisions one + a $0 free subscription. For
                    paid orgs, finds the active or trialing sub on the customer
                    and copies its tier / event bucket / coupon into the row.
                    Idempotent.{' '}
                    <strong className="text-foreground">
                      Doesn't change plans
                    </strong>{' '}
                    — it only mirrors what's already in Stripe.
                  </p>
                </section>

                <section>
                  <h3 className="text-foreground font-semibold">
                    Migrate (per row, also bulk-launchable from selection)
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    Sets the org's subscription to a chosen state — tier, event
                    bucket, billing interval, and optional coupon. Creates a
                    Stripe customer + sub if none exists, otherwise updates the
                    existing sub in place. Use it for: cutover from old Stripe,
                    applying a 100%-off coupon to internal orgs, fixing a wrong
                    tier on a row, or doing real plan changes.
                  </p>
                </section>

                <section>
                  <h3 className="text-foreground font-semibold">
                    Proration toggle (inside the Migrate dialog)
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    <strong className="text-foreground">Off (default):</strong>{' '}
                    immediate change, no Stripe invoice generated. Already-paid
                    invoices are left alone — no refund on downgrade, no extra
                    charge on upgrade. Customer effectively gets the new plan
                    going forward; the next renewal bills at the new pricing.
                    Best for migrations and admin overrides.
                  </p>
                  <p className="text-muted-foreground mt-2">
                    <strong className="text-foreground">On:</strong> mirrors the
                    user-facing plan change rules. Upgrades bill the prorated
                    difference immediately. Downgrades schedule for end of
                    current period (current plan stays active until renewal,
                    then switches). Use for real customer-driven changes where
                    the math should match Stripe's billing behavior.
                  </p>
                </section>

                <section>
                  <h3 className="text-foreground font-semibold">
                    Status column
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    Derived from row state — no live Stripe lookups. Orange "Not
                    synced" means no Stripe customer yet (run sync). Green
                    "Paid" or "Paid (discounted)" means a paid sub exists. Gray
                    "Free synced" means the org has a $0 Stripe sub provisioned.
                  </p>
                </section>

                <section>
                  <h3 className="text-foreground font-semibold">
                    Filter chips
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    "Unsynced" is the most useful one during cutover — surfaces
                    orgs that need an action before migration is complete.
                    "Free" + "Paid" let you scope bulk operations to a category
                    before clicking Select all.
                  </p>
                </section>
              </div>
            </SheetContent>
          </Sheet>
        }
      />
      <PageContent>
        <div className="flex items-center gap-4">
          <div className="relative max-w-sm flex-1">
            <MagnifyingGlassIcon className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search by org name or slug..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="pl-9"
            />
          </div>
          {filtered.length > 0 && (
            <span className="text-muted-foreground text-sm">
              {filtered.length} org{filtered.length !== 1 && 's'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {FILTER_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={filter === opt.value ? 'primary' : 'outline'}
              size="sm"
              onClick={() => {
                setFilter(opt.value);
                setPage(0);
              }}
              title={opt.title}
            >
              {opt.label}
            </Button>
          ))}
        </div>

        {selected.size > 0 && (
          <div className="bg-muted/50 flex items-center justify-between gap-4 rounded border px-3 py-2">
            <span className="text-sm">
              {selected.size} selected
              {selectedFree.length > 0 && selectedPaid.length > 0 && (
                <span className="text-muted-foreground ml-2">
                  ({selectedFree.length} free, {selectedPaid.length} paid)
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelected(new Set())}
                disabled={bulkSyncing}
              >
                Clear
              </Button>
              {selectedFree.length > 0 && (
                <Button
                  size="sm"
                  iconLeft={
                    <ArrowClockwiseIcon
                      className={cn(bulkSyncing && 'animate-spin')}
                    />
                  }
                  onClick={handleBulkSync}
                  disabled={bulkSyncing}
                  title="Provision a Stripe customer + free sub for each selected free org. Already-synced orgs are no-ops."
                >
                  Sync {selectedFree.length} free
                </Button>
              )}
              {selectedRows.length > 0 && (
                <Button
                  size="sm"
                  iconLeft={<ArrowsLeftRightIcon />}
                  onClick={() => openMigrateQueue(selectedRows)}
                  disabled={bulkSyncing}
                  title="Walk through each selected org one at a time, setting tier / bucket / coupon. Works for free, synced, and paid orgs."
                >
                  Migrate {selectedRows.length}…
                </Button>
              )}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="rounded-md border">
            <div className="h-24 text-center leading-[6rem]">Loading...</div>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={pagedRows}
            manualPagination
            enableRowSelection
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            getRowId={(row) => row.organizationId}
          />
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">
              Page {safePage + 1} of {totalPages} · {filtered.length} total
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </PageContent>

      <MigratePaidDialog
        open={migrateTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            // Cancel exits the entire queue, not just the current item.
            setMigrateQueue([]);
            setMigrateIndex(0);
          }
        }}
        subscription={migrateTarget}
        position={
          migrateQueue.length > 1
            ? { current: migrateIndex + 1, total: migrateQueue.length }
            : undefined
        }
        onAdvance={advanceMigrateQueue}
      />

      <GrantCreditsDialog
        open={grantCreditsTarget !== null}
        onOpenChange={(open) => {
          if (!open) setGrantCreditsTarget(null);
        }}
        subscription={grantCreditsTarget}
      />

      <RetentionOverrideDialog
        open={retentionTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRetentionTarget(null);
        }}
        subscription={retentionTarget}
      />

      <Dialog
        open={featureFlagsTarget !== null}
        onOpenChange={(open) => {
          if (!open) setFeatureFlagsTarget(null);
        }}
      >
        <DialogContent className="max-h-[80vh] overflow-x-hidden overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Feature Flags — {featureFlagsTarget?.organizationName}
            </DialogTitle>
          </DialogHeader>
          {featureFlagsTarget && (
            <OrgFeatureFlags
              organizationId={featureFlagsTarget.organizationId}
              onSaved={() => setFeatureFlagsTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
